import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { unitAPI, getProjects } from '../../services/api';
import ReportGenerator from './ReportGenerator';
import './units.css';
import usePermissions from '../../hooks/usePermissions';

const Units = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialProjectId = queryParams.get('projectId');
    const initialStatus = queryParams.get('status');
    const initialOwnership = queryParams.get('ownership');

    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBuilding, setSelectedBuilding] = useState(initialProjectId || 'All');
    const [selectedUnitType, setSelectedUnitType] = useState(initialStatus || 'All'); // Acts as Status Filter
    const [selectedOwnership, setSelectedOwnership] = useState(initialOwnership || 'All'); // Ownership filter
    const [error, setError] = useState(null);
    const [projects, setProjects] = useState([]);
    const [showReportModal, setShowReportModal] = useState(false);
    const { can } = usePermissions();

    // Update filter when URL params change
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const statusParam = queryParams.get('status');
        const projectParam = queryParams.get('projectId');
        const ownershipParam = queryParams.get('ownership');
        if (statusParam) setSelectedUnitType(statusParam);
        if (projectParam) setSelectedBuilding(projectParam);
        if (ownershipParam) setSelectedOwnership(ownershipParam);
    }, [location.search]);

    // Report columns for Units - All sections
    const unitReportColumns = [
        // Basic Info
        { key: 'id', label: 'Unit ID' },
        { key: 'unitNo', label: 'Unit Number' },
        // Location
        { key: 'building', label: 'Building/Project' },
        { key: 'blockTower', label: 'Block/Tower' },
        { key: 'floor', label: 'Floor' },
        // Area Details
        { key: 'area', label: 'Area (SQ FT)' },
        { key: 'carpetArea', label: 'Carpet Area' },
        { key: 'builtUpArea', label: 'Built-up Area' },
        { key: 'superBuiltUpArea', label: 'Super Built-up' },
        // Ownership
        { key: 'ownerName', label: 'Owner Name' },
        { key: 'ownerShare', label: 'Owner Share %' },
        { key: 'ownershipStatus', label: 'Ownership Status' },
        // Tenant
        { key: 'tenantName', label: 'Tenant Name' },
        { key: 'tenantSince', label: 'Tenant Since' },
        // Status
        { key: 'status', label: 'Status' },
        { key: 'statusDesc', label: 'Status Description' },
        { key: 'unitType', label: 'Unit Type' },
        { key: 'unitCategory', label: 'Unit Category' },
        // Additional
        { key: 'facing', label: 'Facing' },
        { key: 'furnishedStatus', label: 'Furnished Status' },
        { key: 'parkingSlots', label: 'Parking Slots' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'created_at', label: 'Created Date' }
    ];

    /* ================= EXPORT CSV ================= */
    const handleExportCSV = () => {
        if (units.length === 0) return;
        const headers = ["Unit No", "Building/Project", "Owner Name", "Tenant Name", "Area (SQ FT)", "Status"];
        const rows = units.map(u => [
            u.unitNo,
            u.building,
            u.ownerName,
            u.tenantName,
            u.area,
            u.status === 'occupied' ? 'Leased' : u.status
        ]);
        
        let csvContent = "data:text/csv;charset=utf-8," 
            + headers.join(",") + "\n"
            + rows.map(r => r.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(",")).join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "units_inventory.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /* ================= FETCH PROJECTS FOR DROPDOWN ================= */
    useEffect(() => {
        const fetchProjectsList = async () => {
            try {
                const response = await getProjects();
                // Ensure we handle the response structure correctly
                const projData = response.data.data || response.data;
                setProjects(projData);
            } catch (err) {
                console.error("Error fetching projects:", err);
            }
        };
        fetchProjectsList();
    }, []);

    /* ================= FETCH UNITS ================= */
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                setLoading(true);


                const params = {};
                if (searchTerm) params.search = searchTerm;
                if (selectedBuilding !== 'All') params.projectId = selectedBuilding;
                if (selectedUnitType !== 'All') params.status = selectedUnitType; // Mapping Unit Type to Status filter
                if (selectedOwnership !== 'All') params.ownership = selectedOwnership; // Ownership filter

                const response = await unitAPI.getUnits(params);
                const data = response.data.data || response.data; // Handle wrapped/unwrapped


                if (!Array.isArray(data)) {
                    throw new Error('API response is not an array');
                }

                const mappedUnits = data.map(unit => ({
                    id: unit.id,
                    unitNo: unit.unit_number || 'N/A',
                    building: unit.building || unit.projects?.project_name || 'N/A',
                    blockTower: unit.block_tower || '-',
                    ownerName: unit.owner_name || 'N/A',
                    tenantName: unit.tenant_name || unit.leases?.[0]?.party_name || (unit.status === 'occupied' ? 'Active Tenant' : '-'),
                    area: unit.chargeable_area || 'N/A',
                    status: unit.status || 'unknown',
                    statusType: unit.status || 'unknown',
                    statusDesc: unit.status === 'vacant' ? 'Available for leasing' : 'Leased'
                }));
                setUnits(mappedUnits);
                setError(null);
            } catch (err) {
                console.error('Fetch error:', err);
                setError(err.message || 'Failed to fetch units');
                setUnits([]);
            } finally {
                setLoading(false);
            }
        };

        // Debounce search
        const timer = setTimeout(() => {
            fetchUnits();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm, selectedBuilding, selectedUnitType, selectedOwnership]);

    // Handlers
    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleBuildingChange = (e) => setSelectedBuilding(e.target.value);
    const handleUnitTypeChange = (e) => setSelectedUnitType(e.target.value);

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedBuilding('All');
        setSelectedUnitType('All');
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this unit?')) {
            try {
                await unitAPI.deleteUnit(id);
                setUnits(units.filter(unit => unit.id !== id));
            } catch (err) {
                console.error('Error deleting unit:', err);
                alert('Failed to delete unit');
            }
        }
    };

    if (loading && units.length === 0) { // Only show full loading screen if no data
        return (
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-content">
                    <p>Loading units...</p>
                </main>
            </div>
        );
    }

    if (error && units.length === 0) {
        return (
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-content">
                    <p>Error fetching units: {error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </main>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <Sidebar />
            <main className="main-content">
                <header className="page-header">
                    <div className="header-left">
                        <div className="breadcrumb">
                            <Link to="/admin/dashboard">HOME</Link> &gt; <Link to="/admin/projects">PROJECTS</Link> &gt; <span className="active">UNITS INVENTORY</span>
                        </div>
                        <h1>Unit Management</h1>
                        <p>Manage your property inventory, availability, and unit details.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {can('view', 'projects') ? (
                            <button onClick={handleExportCSV} className="secondary-btn" style={{ background: '#f8f9fa', color: '#333', border: '1px solid #ddd', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                Export CSV
                            </button>
                        ) : (
                            <button className="secondary-btn" disabled title="No export permission" style={{ background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb', padding: '8px 16px', borderRadius: '4px', cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '4px' }}>🔒 Export CSV</button>
                        )}
                        
                        {can('view', 'projects') ? (
                            <button onClick={() => setShowReportModal(true)} className="secondary-btn" style={{ background: '#eff6ff', color: '#2e66ff', border: '1px solid #bfdbfe', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>
                                Generate Report
                            </button>
                        ) : (
                            <button className="secondary-btn" disabled title="No report permission" style={{ background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb', padding: '8px 16px', borderRadius: '4px', cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '4px' }}>🔒 Generate Report</button>
                        )}
                        
                        {can('edit', 'projects') ? (
                            <Link to="/admin/add-unit" className="primary-btn" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>+ Add Units</Link>
                        ) : (
                            <button className="primary-btn" disabled title="No permission to add units" style={{ opacity: 0.5, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>🔒 Add Units</button>
                        )}

                        {can('edit', 'projects') ? (
                            <Link to="/admin/unit-structure" className="primary-btn" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#10b981' }}>+ Add Unit Structure</Link>
                        ) : (
                            <button className="primary-btn" disabled title="No permission to add units" style={{ backgroundColor: '#10b981', opacity: 0.5, cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>🔒 Add Unit Structure</button>
                        )}
                    </div>
                </header>

                <div className="content-card">
                    {/* Filters Bar */}
                    <div className="filters-bar">
                        <div className="search-wrapper">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            <input type="text" placeholder="Search by Unit No, Tenant, or Building..." value={searchTerm} onChange={handleSearchChange} />
                        </div>
                        <div className="filter-group">
                            <div className="dropdown-filter">
                                <select value={selectedBuilding} onChange={handleBuildingChange}>
                                    <option value="All">All Projects</option>
                                    {projects.map(p => (
                                        <option key={p.id} value={p.id}>{p.project_name}</option>
                                    ))}
                                </select>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </div>
                            <div className="dropdown-filter">
                                <select value={selectedUnitType} onChange={handleUnitTypeChange}>
                                    <option value="">All Status</option>
                                    <option value="vacant">Vacant</option>
                                    <option value="occupied">Leased</option>
                                </select>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            </div>
                        </div>
                        <div className="view-actions">
                            {/* Icons removed as per request */}
                            <button className="text-btn" onClick={handleClearFilters}>Clear filters</button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Unit No</th>
                                    <th>Building/Project</th>
                                    <th>Owner Name</th>
                                    <th>Tenant Name</th>
                                    <th>Area (SQ FT)</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {units.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                                            No units found
                                        </td>
                                    </tr>
                                ) : (
                                    units.map(unit => (
                                        <tr key={unit.id}>
                                            <td className="unit-id">
                                                <Link to={`/admin/view-unit/${unit.id}`} style={{ textDecoration: 'none', color: '#2e66ff', fontWeight: 600 }}>
                                                    {unit.unitNo}
                                                </Link>
                                            </td>
                                            <td>{unit.building}</td>
                                            <td>{unit.ownerName}</td>
                                            <td>{unit.tenantName}</td>
                                            <td>{unit.area}</td>
                                            <td>
                                                <span className={`status-badge ${unit.status}`}>
                                                    {unit.status === 'occupied' ? 'Leased' : unit.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-icon-wrapper">
                                                    {can('view', 'projects') ? (
                                                        <Link to={`/admin/view-unit/${unit.id}`} className="action-icon-btn view" title="View">
                                                            <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                                        </Link>
                                                    ) : (
                                                        <button className="action-icon-btn" disabled title="No view permission" style={{ opacity: 0.4, cursor:'not-allowed', fontSize:'14px' }}>🔒</button>
                                                    )}
                                                    {can('edit', 'projects') ? (
                                                        <Link to={`/admin/edit-unit/${unit.id}`} className="action-icon-btn edit" title="Edit">
                                                            <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                                        </Link>
                                                    ) : (
                                                        <button className="action-icon-btn" disabled title="No edit permission" style={{ opacity: 0.4, cursor:'not-allowed', fontSize:'14px' }}>🔒</button>
                                                    )}
                                                    {can('delete', 'projects') ? (
                                                        <button className="action-icon-btn delete" onClick={() => handleDelete(unit.id)} title="Delete">
                                                            <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                                        </button>
                                                    ) : (
                                                        <button className="action-icon-btn" disabled title="No delete permission" style={{ opacity: 0.4, cursor:'not-allowed', fontSize:'14px' }}>🔒</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="pagination">
                        <span>Rows per page: 10 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></span>
                        <span>1—{units.length} of {units.length}</span>
                        <div className="page-nav">
                            <button disabled>&lt;</button>
                            <span>1</span>
                            <button>&gt;</button>
                        </div>
                    </div>
                </div>
            </main>

            {showReportModal && (
                <ReportGenerator
                    title="Units Report"
                    data={units}
                    columns={unitReportColumns}
                    onClose={() => setShowReportModal(false)}
                />
            )}
        </div>
    );
};

export default Units;